"use client"

import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import { dbCharacterToCharacter } from "@/shared/lib/supabase/mappers"
import { queryKeys } from "@/shared/lib/query-keys"
import { characters as staticCharacters } from "@/features/characters/data"
import {
  defaultLocale,
  getCharacterContentLocaleForUiLocale,
  type UiLocale,
} from "@/shared/lib/i18n/config"
import { useCurrentLocale } from "@/shared/hooks/use-current-locale"
import type { Character, CharacterGender, CharacterStyle } from "@/shared/types/character"
import type { CharacterRow, LocalizedPublicCharacterRow } from "@/shared/lib/supabase/types"
import type { SupabaseClient } from "@supabase/supabase-js"
import { getOptionalSupabasePublishableKey } from "@/shared/lib/supabase/keys"

interface UseCharactersFilters {
  gender?: CharacterGender | "all"
  style?: CharacterStyle
  search?: string
  official?: boolean
  limit?: number
  offset?: number
}

const MAX_RETRIES = 3
const RETRY_DELAYS_MS = [0, 1000, 3000]
const CHARACTER_LIST_STALE_TIME_MS = 1000 * 60 * 10
const CHARACTER_LIST_GC_TIME_MS = 1000 * 60 * 45
const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL
const SUPABASE_PUBLISHABLE_KEY = getOptionalSupabasePublishableKey()
let supabaseClientPromise: Promise<SupabaseClient> | null = null

async function getSupabaseClient(): Promise<SupabaseClient> {
  // PLE-945: forget a failed load so the next call tries again. Turbopack can
  // register a loader body for this import that assumes the SDK chunk has
  // already run (vercel/next.js#98099); if it has not, the import rejects with
  // "module factory is not available". The runtime does not cache that
  // failure, so a later attempt succeeds once the chunk has registered.
  supabaseClientPromise ??= import("@/shared/lib/supabase/client")
    .then(({ createClient }) => createClient())
    .catch((error: unknown) => {
      supabaseClientPromise = null
      throw error
    })
  return supabaseClientPromise
}

async function fetchPublicSupabaseRest<T>(
  path: string,
  init?: RequestInit
): Promise<T> {
  if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
    throw new Error("Public Supabase configuration is unavailable")
  }

  const response = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    ...init,
    headers: {
      apikey: SUPABASE_PUBLISHABLE_KEY,
      ...init?.headers,
    },
  })
  if (!response.ok) {
    let payload: unknown
    try {
      payload = await response.json()
    } catch {
      payload = null
    }

    const details =
      payload && typeof payload === "object"
        ? (payload as Record<string, unknown>)
        : null
    const message =
      typeof details?.message === "string"
        ? details.message
        : `Public Supabase request failed (${response.status})`
    const error = new Error(message) as Error & {
      status: number
      code?: string
      details?: string
      hint?: string
    }
    error.status = response.status
    if (typeof details?.code === "string") error.code = details.code
    if (typeof details?.details === "string") error.details = details.details
    if (typeof details?.hint === "string") error.hint = details.hint
    throw error
  }
  return response.json() as Promise<T>
}

function fetchLocalizedPublicCharacterRows(
  filters: UseCharactersFilters,
  locale: UiLocale,
  characterId: string | null = null
): Promise<LocalizedPublicCharacterRow[]> {
  return fetchPublicSupabaseRest<LocalizedPublicCharacterRow[]>(
    "rpc/get_localized_public_characters",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        p_locale: getCharacterContentLocaleForUiLocale(locale),
        p_search: filters.search?.trim() || null,
        p_gender:
          filters.gender && filters.gender !== "all" ? filters.gender : null,
        p_style: filters.style ?? null,
        p_official: filters.official ?? null,
        p_character_id: characterId,
        p_limit: filters.limit ?? null,
        p_offset: filters.offset ?? 0,
      }),
    }
  )
}

type CreatorProfilePreview = {
  username: string | null
  display_name: string | null
}

type CharacterRowWithCreatorPreview = CharacterRow & {
  profiles?: CreatorProfilePreview | null
}

async function attachCreatorProfiles(
  supabase: SupabaseClient,
  rows: CharacterRow[]
): Promise<CharacterRowWithCreatorPreview[]> {
  const creatorIds = Array.from(
    new Set(
      rows
        .map((row) => row.created_by)
        .filter((id): id is string => Boolean(id))
    )
  )

  if (creatorIds.length === 0) return rows

  const { data, error } = await supabase
    .from("profiles")
    .select("id, username, display_name")
    .in("id", creatorIds)

  if (error || !data) return rows

  const profileById = new Map(
    data.map((profile) => [
      profile.id,
      {
        username: profile.username,
        display_name: profile.display_name,
      },
    ])
  )

  return rows.map((row) => ({
    ...row,
    profiles: row.created_by ? profileById.get(row.created_by) ?? null : null,
  }))
}

async function withRetry<T>(fn: () => Promise<T>, retries = MAX_RETRIES): Promise<T> {
  let lastError: unknown
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const delay = RETRY_DELAYS_MS[attempt] ?? 0
      if (delay > 0) await new Promise((r) => setTimeout(r, delay))
      return await fn()
    } catch (err) {
      lastError = err
      if (isUnresolvableIdError(err)) throw err
    }
  }
  throw lastError
}

async function attachPreviewVideos(
  characters: Character[]
): Promise<Character[]> {
  const characterIds = characters.map((character) => character.id)
  if (characterIds.length === 0) return characters

  const params = new URLSearchParams({
    select: "character_id,media_url,generation_params,created_at",
    type: "eq.video",
    status: "eq.active",
    character_id: `in.(${characterIds.join(",")})`,
    order: "created_at.desc",
  })
  type PreviewVideoRow = {
    character_id: string | null
    media_url: string | null
    generation_params: Record<string, unknown> | null
  }

  let data: PreviewVideoRow[]
  try {
    data = await fetchPublicSupabaseRest<PreviewVideoRow[]>(
      `gallery_posts?${params.toString()}`
    )
  } catch {
    return characters
  }

  const videoByCharacterId = new Map<string, string>()
  const reverseVideoByCharacterId = new Map<string, string>()

  for (const row of data) {
    if (row.character_id && row.media_url && !videoByCharacterId.has(row.character_id)) {
      videoByCharacterId.set(row.character_id, row.media_url)

      const reverseMediaUrl =
        typeof row.generation_params === "object" &&
        row.generation_params !== null &&
        "reverse_media_url" in row.generation_params &&
        typeof row.generation_params.reverse_media_url === "string"
          ? row.generation_params.reverse_media_url
          : null

      if (reverseMediaUrl) {
        reverseVideoByCharacterId.set(row.character_id, reverseMediaUrl)
      }
    }
  }

  return characters.map((character) => ({
    ...character,
    previewVideoUrl: videoByCharacterId.get(character.id) ?? character.previewVideoUrl ?? null,
    previewReverseVideoUrl:
      reverseVideoByCharacterId.get(character.id) ?? character.previewReverseVideoUrl ?? null,
  }))
}

async function fetchLocalizedCharacters(
  filters: UseCharactersFilters = {},
  locale: UiLocale = defaultLocale
): Promise<Character[]> {
  try {
    const result = await withRetry(async () => {
      const rows = await fetchLocalizedPublicCharacterRows(filters, locale)
      return attachPreviewVideos(rows.map(dbCharacterToCharacter))
    })

    if (result && result.length > 0) return result
  } catch {
    // Supabase unavailable after retries — fall through to static data
  }

  let result = [...staticCharacters]
  if (filters.gender && filters.gender !== "all") {
    result = result.filter((c) => c.gender === filters.gender)
  }
  if (filters.style) {
    result = result.filter((c) => c.style === filters.style)
  }
  if (filters.search?.trim()) {
    const term = filters.search.trim().toLowerCase()
    result = result.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.tagline.toLowerCase().includes(term) ||
        c.description.toLowerCase().includes(term)
    )
  }
  // Deliberately do NOT filter the static fallback by `official`. The static
  // seed is the platform's own curated set and carries no `isOfficial` flag,
  // so filtering here would collapse an RPC failure to an empty grid
  // ("No characters found") on the Home landing surface. When the RPC
  // succeeds, `p_official` already scopes the result server-side.
  const offset = filters.offset ?? 0
  return filters.limit == null
    ? result.slice(offset)
    : result.slice(offset, offset + filters.limit)
}

export function useInfiniteCharactersList(
  filters: Omit<UseCharactersFilters, "limit" | "offset"> = {},
  pageSize = 34
) {
  const locale = useCurrentLocale()

  return useInfiniteQuery({
    queryKey: [
      ...queryKeys.characters.list({
        ...(filters as Record<string, unknown>),
        locale,
      }),
      "infinite",
      pageSize,
    ],
    queryFn: ({ pageParam }) =>
      fetchLocalizedCharacters(
        { ...filters, limit: pageSize, offset: pageParam },
        locale
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) =>
      lastPage.length === pageSize ? pages.length * pageSize : undefined,
    staleTime: CHARACTER_LIST_STALE_TIME_MS,
    gcTime: CHARACTER_LIST_GC_TIME_MS,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  })
}

/**
 * Postgres `invalid_text_representation` — the id was not a valid UUID
 * (e.g. a slug like `/chat/priya-sharma`). Such an id can never resolve
 * against the UUID `id` column, so it is a genuine not-found, not a
 * transient failure worth retrying.
 */
function isUnresolvableIdError(err: unknown): boolean {
  return (err as { code?: string } | null)?.code === "22P02"
}

// Any id that is not a UUID (static ids like "f1", slugs) can never match the
// UUID `id` column; querying it only produces a 400 (22P02) from Supabase.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Resolve a character by id.
 *
 * Returns `null` only for a *genuine* not-found: the query succeeded with zero
 * visible rows, or the id can never resolve (non-UUID). Transient failures
 * (network blips, or the auth session still being established) are re-thrown so
 * React Query keeps the query in a loading/error state and retries — instead of
 * collapsing to `null` and rendering a dead-end "Character not found".
 */
export async function fetchCharacter(
  id: string,
  locale: UiLocale = defaultLocale
): Promise<Character | null> {
  if (!UUID_RE.test(id)) {
    return staticCharacters.find((c) => c.id === id) ?? null
  }

  try {
    const data = await withRetry(async () => {
      const publicRows = await fetchLocalizedPublicCharacterRows(
        { limit: 1 },
        locale,
        id
      )
      const publicRow = publicRows[0]
      if (publicRow) return publicRow

      // Preserve the original owner-visible detail behavior for private rows.
      // The localized catalog RPC is public-only by design.
      const supabase = await getSupabaseClient()
      const { data: directRow, error: directError } = await supabase
        .from("characters")
        .select("*")
        .eq("id", id)
        .eq("status", "active")
        .maybeSingle()
      if (directError) throw directError
      if (!directRow) return null

      const [character] = await attachCreatorProfiles(supabase, [directRow])
      return character ?? null
    })

    // Query succeeded: a row → character; no row → fall through to static/null.
    if (data) {
      const [character] = await attachPreviewVideos([dbCharacterToCharacter(data)])
      return character ?? null
    }
  } catch (err) {
    const staticMatch = staticCharacters.find((c) => c.id === id)
    if (staticMatch) return staticMatch
    // A non-UUID id (slug) can never resolve — treat as a real not-found.
    if (isUnresolvableIdError(err)) return null
    // Otherwise this was a transient failure (network / auth-timing). Re-throw
    // so React Query retries and the UI shows loading/retry, not "not found".
    throw err
  }

  return staticCharacters.find((c) => c.id === id) ?? null
}

const CHARACTER_DETAIL_POLL_INTERVAL_MS = 3000

export function useCharacter(
  id: string,
  options?: {
    pollUntilFound?: boolean
    isReady?: (character: Character | null | undefined) => boolean
  }
) {
  const locale = useCurrentLocale()

  return useQuery({
    queryKey: queryKeys.characters.detail(id, locale),
    queryFn: () => fetchCharacter(id, locale),
    enabled: !!id,
    // fetchCharacter() already runs its own withRetry() loop, which is the
    // auth-settling window. Don't stack a query-level retry on top of it —
    // nesting the two would keep users on the loading skeleton across several
    // full retry cycles before the recoverable retry UI can appear. On a
    // persistent failure we surface the error promptly; the retry UI offers a
    // manual refetch.
    retry: false,
    refetchInterval: options?.pollUntilFound
      ? (query) => {
          const isReady = options.isReady ?? Boolean
          return isReady(query.state.data) ? false : CHARACTER_DETAIL_POLL_INTERVAL_MS
        }
      : undefined,
  })
}
